import { fetchClientData, resetClientData } from '@/redux/ducks/client/detail'
import { fetchCompanyData } from '@/redux/ducks/settings/company'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import getCollaborationRequestHeader from '@/modules/document/_helpers/getCollaborationRequestHeader'
import settingsConfig from '@/modules/settings/config'
import { SUBSCRIPTION_MODULES } from '@/modules/base/util/constants'
import request from '@/modules/base/util/request'

const convertName = (name) => {
  return name.replace('_', '').toLowerCase()
}

const getModuleAccessBySubscriptionDetails = (activeDetails) => {
  let modules = {}
  for (const key of Object.values(SUBSCRIPTION_MODULES)) {
    const access = activeDetails.some(item => item.name.toLowerCase() === convertName(key))
    modules = { ...modules, [key]: access }
  }

  return modules
}

const useClientSetting = (props) => {
  const dispatch = useDispatch()

  const { doFetch = false, tenantCollaborationId } = props || {}
  const [shouldRefresh, setShouldRefresh] = useState(doFetch)
  const [moduleAccess, setModuleAccess] = useState({})
  const [subscriptions, setSubscriptions] = useState([])
  const client = useSelector(data => data.client.detail?.data)
  const isLoading = useSelector(data => data.client?.detail?.isLoading)
  const { id: currentTenantId } = useSelector(store => store.tenant.info)
  const collaboratorTenantId
  = client.inviterTenantId !== currentTenantId
    ? client.inviterTenantId
    : client.inviteeTenantId

  // Use tenantCollaborationId if provided, otherwise use client from Redux
  const collaborationId = tenantCollaborationId || client?.id
  const isClientSetting = !!Object.keys(client).length || !!tenantCollaborationId
  const refresh = () => setShouldRefresh(true)
  const reset = () => dispatch(resetClientData())

  const useResetClientData = () => {
    useEffect(() => {
      return () => reset()
    }, [])
  }

  const getModuleAccess = async(clientId) => {
    const response = await request(settingsConfig.getSubscriptionDetails,
      {
        auth: true,
        isConcurrent: true,
        headers: getCollaborationRequestHeader(clientId),
      })

    if (response) {
      setModuleAccess(getModuleAccessBySubscriptionDetails(response?.body?.data?.activeDetails))
    }
  }

  useEffect(() => {
    const fetchSubscriptionDetails = async() => {
      await getModuleAccess(client?.id)
      if (!collaborationId) return

      const response = await request(settingsConfig.getSubscriptionDetails,
        {
          auth: true,
          isConcurrent: true,
          headers: getCollaborationRequestHeader(collaborationId),
        })

      if (response) {
        const { subscriptions: clientSubscriptions, activeDetails } = response?.body?.data || {}
        setModuleAccess(getModuleAccessBySubscriptionDetails(activeDetails))
        setSubscriptions(clientSubscriptions || [])
      }
    }

    if (isClientSetting && shouldRefresh && collaborationId) {
      setShouldRefresh(false)
      fetchSubscriptionDetails()
      // Only fetch full client data if we have a client in Redux (not just tenantCollaborationId)
      if (client?.id && !tenantCollaborationId) {
        dispatch(fetchClientData(client?.id, (newClientData) => {
          if (newClientData && newClientData.isAllowed) {
            dispatch(fetchCompanyData(client?.id))
          }
        }))
      }
    }
  }, [client, shouldRefresh, tenantCollaborationId, collaborationId, isClientSetting])

  return {
    client,
    refresh,
    getModuleAccess,
    shouldRefresh,
    useResetClientData,
    collaboratorTenantId,
    isClientSetting,
    moduleAccess,
    subscriptions,
    isLoading,
  }
}

export default useClientSetting
